import React, {
    SetStateAction,
    useState,
    ReactNode,
    isValidElement
} from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import {
    IconNameTypes,
    ProductKeyTypes,
    Row
} from "../../../../libraries/wpl/foundations/src/index";
import { useLocation } from "../../../../utils/location-provider";
import { Card, CardProps } from "../../card/src/index";
import { HeaderTagTypes } from "../../content-block/src";
import { ContentBlock, OneLinkProps } from "../../content-block/src/index";
import { Image, ImageProps } from "../../image/src/index";
import { Link } from "../../link/src/index";
import { Video, VideoProps } from "../../video/src/index";

const TabGroupStyle = css({
    [media.below.sm]: {
        display: "none"
    },
    // This line is being affected by new linting rules, no edits have been made to the code
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".tab-group-inner": {
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: "16px"
    }
});

const quoteSidebar = css({
    textAlign: "left"
});

const tabPanel = css({
    margin: "20px auto 0",
    [`&[aria-hidden="true"]`]: {
        display: "none"
    }
});

const contentBlockStyle = css({
    maxWidth: "100%",
    margin: "0 auto 24px",
    [media.above.md]: {
        width: "768px"
    }
});

export interface TabColorProps {
    defaultBG?: string;
    defaultText?: string;
    hoverBG?: string;
    hoverText?: string;
    activeBG?: string;
    activeText?: string;
    quoteColor?: string;
}

export interface ImageDisplayProps {
    imageProps: ImageProps;
}

export interface VideoDisplayProps {
    videoProps: VideoProps;
}

export type DisplayProps = ImageDisplayProps | VideoDisplayProps | JSX.Element;

export interface IndividualTabProps {
    textLabel: string;
    isForceDisplayTextLabelOnPill?: boolean;
    id: string;
    dataLabelEnglish?: string;
}

export interface IndividualTabPropsCustom extends IndividualTabProps {
    component: JSX.Element | (() => JSX.Element);
    iconBefore?: JSX.Element;
}

export interface IndividualTabPropsCards extends IndividualTabProps {
    cards: CardProps[];
}

export interface IndividualTabPropsQuote extends IndividualTabProps {
    display: DisplayProps;
    bodyText?: ReactNode;
    headerTextType?: HeaderTagTypes;
    headerText?: ReactNode;
    iconName?: IconNameTypes;
    iconColor?: string;
    quoteImageProps?: ImageProps;
    quote?: string;
    isDisplayQuoteInSmScreen?: boolean;
    name?: string;
    company?: ReactNode;
    ctaProps?: OneLinkProps;
}

export interface IndividualTabPropsImageFullWidth extends IndividualTabProps {
    display: DisplayProps;
    headerText?: ReactNode;
    bodyText?: ReactNode;
}

export type TabsTypes =
    | IndividualTabPropsCustom
    | IndividualTabPropsCards
    | IndividualTabPropsQuote
    | IndividualTabPropsImageFullWidth;

export type TabContentTypes =
    | "custom"
    | "cards"
    | "quoteTextImage"
    | "textImageFullWidth"
    | "iconTextImage";

export interface TabGroupProps {
    tabContentType: TabContentTypes;
    tabs: TabsTypes[];
    id: string;
    label: string;
    dataProductKey: ProductKeyTypes;
    colors?: TabColorProps;
    onMobileChangeHandler?: React.Dispatch<SetStateAction<object>>;
    onDesktopClickHandler?: React.Dispatch<SetStateAction<object>>;
}

export interface CardNums {
    6: number;
    5: number;
    4: number;
    3: number;
    2: number;
}

const cardRules: CardNums = {
    6: 1,
    5: 2,
    4: 2,
    3: 3,
    2: 4
};

const CardStyleDisplay = (tab: IndividualTabPropsCards) => {
    return (
        <Row>
            {tab.cards.map((card: CardProps, index: number) => {
                return (
                    <Card
                        key={index}
                        {...card}
                        aspectRatio={tab.cards.length < 3 ? "16/9" : "3/2"}
                    />
                );
            })}
        </Row>
    );
};

export const renderTabContent = (props: {
    tab: IndividualTabPropsQuote | IndividualTabPropsImageFullWidth;
}) => {
    const tabDisplay = props.tab.display;
    let elementToDisplay = null;

    if ("videoProps" in tabDisplay && tabDisplay.videoProps.videoLink?.length) {
        elementToDisplay = <Video {...tabDisplay.videoProps} />;
    } else if (
        "imageProps" in tabDisplay &&
        tabDisplay.imageProps.src?.length &&
        tabDisplay.imageProps.alt?.length
    ) {
        elementToDisplay = <Image {...tabDisplay.imageProps} />;
    } else if (isValidElement(tabDisplay)) {
        elementToDisplay = tabDisplay;
    }

    return elementToDisplay;
};

export const Quote = (props: {
    image?: JSX.Element;
    quote?: string;
    name?: string;
    company?: ReactNode;
    colors?: TabColorProps;
    isDisplayInSmScreen?: boolean;
}) => {
    const backgroundImageColor = props.colors?.quoteColor
        ? `linear-gradient(90deg, transparent, ${props.colors.quoteColor})`
        : "transparent";
    const quoteWrapperClass = props.isDisplayInSmScreen
        ? "quote-wrapper isDisplayInSmScreen"
        : "quote-wrapper";
    const quoteStyle = css({
        "textAlign": "left",
        "margin": "0 0 24px",
        "padding": "30px 50px 30px 0",
        "borderRadius": "0 100px 100px 0 / 0 50% 50% 0",
        "backgroundImage": backgroundImageColor,
        // This line is being affected by new linting rules, no edits have been made to the code
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".quote-blurb": {
            marginInlineStart: 0,
            fontSize: "1.2rem",
            fontFamily: "var(--bxp-charlie-display-font-family)"
        },
        // This line is being affected by new linting rules, no edits have been made to the code
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".quote-name": {
            fontWeight: "bold",
            margin: "20px 0 0",
            textTransform: "uppercase"
        },
        // This line is being affected by new linting rules, no edits have been made to the code
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".quote-company": {
            margin: 0,
            fontSize: "0.875rem",
            textTransform: "uppercase"
        },
        [media.below.sm]: {
            "display": "none",
            "padding": "0px",
            "&.isDisplayInSmScreen": {
                display: "block"
            }
        }
    });

    return (
        <div className={quoteWrapperClass} css={quoteStyle}>
            {props.image ? props.image : null}
            <p className="quote-blurb">{props.quote}</p>
            {props.name ? <p className="quote-name">{props.name}</p> : null}
            {props.company ? (
                <p className="quote-company">{props.company}</p>
            ) : null}
        </div>
    );
};

const QuoteTextImage = (
    tab: IndividualTabPropsQuote,
    colors?: TabColorProps
) => {
    return (
        <Row layout="4-8">
            <div css={quoteSidebar}>
                {tab.headerText ? (
                    <ContentBlock
                        css={contentBlockStyle}
                        headerText={tab.headerText}
                        bodyText={tab.bodyText}
                        headerType={tab.headerTextType ?? "h3"}
                        iconName={tab.iconName}
                    />
                ) : null}
                {tab.quote ? (
                    <Quote
                        image={
                            tab.quoteImageProps && (
                                <Image {...tab.quoteImageProps} />
                            )
                        }
                        quote={tab.quote}
                        name={tab.name}
                        company={tab.company}
                        isDisplayInSmScreen={
                            tab.isDisplayQuoteInSmScreen || false
                        }
                        colors={colors}
                    />
                ) : null}

                {tab.ctaProps && tab.ctaProps.primaryLinkProps ? (
                    <Link {...tab.ctaProps.primaryLinkProps} />
                ) : null}
            </div>
            {renderTabContent({ tab })}
        </Row>
    );
};

const TextImageFullWidth = (tab: IndividualTabPropsImageFullWidth) => {
    return (
        <Row>
            <div>
                {tab.headerText ? (
                    <div className="content-block" css={contentBlockStyle}>
                        <ContentBlock
                            headerText={tab.headerText}
                            bodyText={tab.bodyText}
                            headerType="h2"
                            isCentered={true}
                        />
                    </div>
                ) : null}
                {renderTabContent({ tab })}
            </div>
        </Row>
    );
};

const TabContent = (
    tabContentType: TabContentTypes,
    tab: TabsTypes,
    tabsLength: number,
    colors?: TabColorProps
) => {
    const cardRule = cardRules[tabsLength as keyof CardNums];

    switch (tabContentType) {
        case "custom":
            if (!("component" in tab)) {
                throw new Error("Must provide a `customComponent` JSX node");
            }
            if (typeof tab.component === "function") {
                const TabComponent = tab.component;
                return <TabComponent />;
            }
            return tab.component;
        case "cards":
            if (!("cards" in tab)) {
                throw new Error(
                    "Must provide an array of card values in the `cards` tab prop"
                );
            } else if (tab.cards.length > cardRule) {
                throw new Error(
                    `For a group of ${tabsLength} tabs, no more than ${cardRule} cards per tab`
                );
            }
            return CardStyleDisplay(tab);
        case "quoteTextImage":
            if (!("display" in tab)) {
                throw new Error("Please provide image or video props");
            }
            return QuoteTextImage(tab, colors);
        case "textImageFullWidth":
            if (!("display" in tab)) {
                throw new Error("Please provide image or video props");
            }
            return TextImageFullWidth(tab);
        default:
            return null;
    }
};

const updateURL = (tabId?: string) => {
    const newParams = new URLSearchParams(window.location.search);
    if (tabId) {
        newParams.set("tab", tabId);
        window.history.pushState({}, "", `?${newParams.toString()}`);
    }
};

interface TabStyle {
    activeBGColor: string;
    hoverBGColor: string;
    backgroundColor: string;
    color: string;
    borderColor: string;
    hoverBG: string;
    hoverText: string;
}

const getTabStyle = (
    colors: TabColorProps | undefined,
    isActive: boolean
): TabStyle => {
    if (isActive) {
        return {
            backgroundColor: colors?.activeBG || "#b2d4ff",
            color: colors?.activeText || "#091e42",
            borderColor: colors?.activeBG || "#b2d4ff",
            hoverBG: colors?.activeBG || "#b2d4ff",
            hoverText: colors?.activeText || "#091e42",
            activeBGColor: colors?.activeBG || "#b2d4ff",
            hoverBGColor: colors?.activeBG || "#b2d4ff"
        };
    }
    return {
        backgroundColor: colors?.defaultBG || "#f4f5f7",
        color: colors?.defaultText || "#091e42",
        borderColor: "transparent",
        hoverBG: colors?.hoverBG || "#ebecf0",
        hoverText: colors?.hoverText || "#091e42",
        activeBGColor: colors?.activeBG || "#b2d4ff",
        hoverBGColor: colors?.hoverBG || "#ebecf0"
    };
};

interface TabGroupInnerProps {
    index: number;
    currentTab: number;
    tab: TabsTypes;
    colors?: TabColorProps;
    dataProductKey: ProductKeyTypes;
    onDesktopClickHandler?: React.Dispatch<SetStateAction<object>>;
    setCurrentTab: React.Dispatch<SetStateAction<number>>;
}

const TabGroupInner = ({
    index,
    currentTab,
    tab,
    colors,
    dataProductKey,
    onDesktopClickHandler,
    setCurrentTab
}: TabGroupInnerProps) => {
    const isActive = index === currentTab;
    const {
        activeBGColor,
        hoverBGColor,
        backgroundColor,
        color,
        borderColor,
        hoverBG,
        hoverText
    }: TabStyle = getTabStyle(colors, isActive);

    const tabStyle = css({
        "cursor": "pointer",
        "borderRadius": "50px",
        "boxSizing": "border-box",
        "backgroundClip": "padding-box",
        "padding": "10px 30px",
        "appearance": "none",
        "borderStyle": "solid",
        "borderWidth": "2px",
        "borderColor": "transparent",
        "fontFamily": "var(--bxp-charlie-display-font-family)",
        "letterSpacing": "0.04rem",
        "&.select": {
            margin: "0 auto",
            display: "block",
            width: "100%"
        },
        [media.above.md]: {
            fontSize: "1.25rem"
        },
        [media.below.md]: {
            fontSize: "0.85rem"
        },
        "backgroundColor": `${backgroundColor}`,
        "color": color,
        "&:hover, &:focus": {
            backgroundColor: `${hoverBG}`,
            color: `${hoverText}`,
            borderColor: `${borderColor}`
        },
        "&:focusVisible": {
            outline: 0,
            borderColor: `${activeBGColor}`
        },
        "&:active": {
            borderColor: `${hoverBGColor}`
        }
    });

    return (
        <button
            type="button"
            role="tab"
            key={`tab-${index}-${tab.id}`}
            value={tab.textLabel}
            css={tabStyle}
            aria-selected={index === currentTab}
            aria-controls={tab.id}
            id={tab.id}
            data-event-component="linkButton"
            data-event-container="tabContent"
            data-event="clicked"
            data-label={tab.textLabel}
            data-label-english={tab.dataLabelEnglish}
            data-product-key={dataProductKey}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                setCurrentTab(index);
                updateURL(tab.id);
                if (onDesktopClickHandler) {
                    onDesktopClickHandler(event);
                }
            }}
        >
            {(tab as IndividualTabPropsCustom).iconBefore ??
                (tab as IndividualTabPropsCustom).iconBefore}
            {tab.isForceDisplayTextLabelOnPill
                ? tab.textLabel
                : "headerText" in tab
                ? tab.headerText
                : tab.textLabel}
        </button>
    );
};

interface TabOptionProps {
    index: number;
    currentTab: number;
    tab: TabsTypes;
    colors?: TabColorProps;
    dataProductKey: string;
}

const TabOption = ({
    index,
    currentTab,
    tab,
    colors,
    dataProductKey
}: TabOptionProps) => {
    const isActive = index === currentTab;
    const {
        activeBGColor,
        hoverBGColor,
        backgroundColor,
        color,
        borderColor,
        hoverBG,
        hoverText
    }: TabStyle = getTabStyle(colors, isActive);

    const tabStyle = css({
        "cursor": "pointer",
        "borderRadius": "50px",
        "boxSizing": "border-box",
        "backgroundClip": "padding-box",
        "padding": "10px 30px",
        "appearance": "none",
        "borderStyle": "solid",
        "borderWidth": "2px",
        "borderColor": "transparent",
        "fontFamily": "var(--bxp-charlie-display-font-family)",
        "letterSpacing": "0.04rem",
        "&.select": {
            margin: "0 auto",
            display: "block",
            width: "100%"
        },
        [media.above.md]: {
            fontSize: "1.25rem"
        },
        [media.below.md]: {
            fontSize: "0.85rem"
        },
        "backgroundColor": `${backgroundColor}`,
        "color": color,
        "&:hover, &:focus": {
            backgroundColor: `${hoverBG}`,
            color: `${hoverText}`,
            borderColor: `${borderColor}`
        },
        "&:focusVisible": {
            outline: 0,
            borderColor: `${activeBGColor}`
        },
        "&:active": {
            borderColor: `${hoverBGColor}`
        }
    });

    return (
        <option
            data-index={index}
            data-id={tab.id}
            data-event-component="linkButton"
            data-event-container="tabContent"
            data-event="clicked"
            data-label={tab.textLabel}
            data-product-key={dataProductKey}
            value={tab.textLabel}
            key={`option-${index}-${tab.id}`}
            css={tabStyle}
            aria-selected={index === currentTab}
            aria-describedby={`tab-${tab.id}`}
        >
            {tab.isForceDisplayTextLabelOnPill
                ? tab.textLabel
                : "headerText" in tab
                ? tab.headerText
                : tab.textLabel}
        </option>
    );
};

interface TabSelectProps {
    id: string;
    currentTab: number;
    tabs: TabsTypes[];
    colors?: TabColorProps;
    updateURL: (id: string | undefined) => void;
    onMobileChangeHandler?: (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => void;
    setCurrentTab: React.Dispatch<SetStateAction<number>>;
    dataProductKey: string;
}

interface tabsMapProps {
    [index: string]: TabsTypes;
}

const TabSelect = ({
    id,
    currentTab,
    tabs,
    colors,
    updateURL,
    onMobileChangeHandler,
    setCurrentTab,
    dataProductKey
}: TabSelectProps) => {
    const isActive = true; // Always active for TabSelect
    const {
        activeBGColor,
        hoverBGColor,
        backgroundColor,
        color,
        borderColor,
        hoverBG,
        hoverText
    }: TabStyle = getTabStyle(colors, isActive);

    const tabStyle = css({
        "cursor": "pointer",
        "borderRadius": "50px",
        "boxSizing": "border-box",
        "backgroundClip": "padding-box",
        "padding": "10px 30px",
        "appearance": "none",
        "borderStyle": "solid",
        "borderWidth": "2px",
        "borderColor": "transparent",
        "fontFamily": "var(--bxp-charlie-display-font-family)",
        "letterSpacing": "0.04rem",
        "&.select": {
            margin: "0 auto",
            display: "block",
            width: "100%"
        },
        [media.above.md]: {
            fontSize: "1.25rem"
        },
        [media.below.md]: {
            fontSize: "0.85rem"
        },
        "backgroundColor": `${backgroundColor}`,
        "color": color,
        "&:hover, &:focus": {
            backgroundColor: `${hoverBG}`,
            color: `${hoverText}`,
            borderColor: `${borderColor}`
        },
        "&:focusVisible": {
            outline: 0,
            borderColor: `${activeBGColor}`
        },
        "&:active": {
            borderColor: `${hoverBGColor}`
        }
    });

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedIndex =
            event?.target?.selectedOptions[0]?.dataset["index"];
        const selectedId = event?.target?.selectedOptions[0]?.dataset["id"];
        setCurrentTab(Number(selectedIndex));
        updateURL(selectedId);
        if (onMobileChangeHandler) {
            onMobileChangeHandler(event);
        }
    };

    return (
        <select
            id={id}
            className="select"
            css={tabStyle}
            value={tabs[currentTab]?.textLabel || tabs[0]?.textLabel}
            onChange={handleChange}
        >
            {tabs.map((tab: TabsTypes, index: number) => {
                return (
                    <TabOption
                        key={`option-${index}-${tab.id}`}
                        index={index}
                        tab={tab}
                        colors={colors}
                        dataProductKey={dataProductKey}
                        currentTab={currentTab}
                    />
                );
            })}
        </select>
    );
};

export const TabGroup = (props: TabGroupProps) => {
    const {
        tabContentType,
        tabs,
        colors,
        id,
        label,
        dataProductKey,
        onDesktopClickHandler,
        onMobileChangeHandler
    } = props;

    const location = useLocation();

    const [currentTab, setCurrentTab] = useState(() => {
        const params = new URLSearchParams(
            location.search ?? window.location.search
        );
        const paramID = params.get("tab");
        const matchingTab = tabs.findIndex((tab) => {
            return tab.id === paramID;
        });

        if (matchingTab >= 0) {
            return matchingTab;
        }
        return 0;
    });

    if (!id || !label) {
        throw new Error(
            '"id" and "label" props are required for accessibility!'
        );
    }

    if (tabs.length > 6 || tabs.length < 2) {
        throw new Error(
            "Must have a minimum of 2 and a maximum of 6 tabs per group!"
        );
    }

    const tabsMap = tabs.reduce(function (map, obj, index) {
        map[index] = obj;
        return map;
    }, {} as tabsMapProps);

    const MobileSelect = () => {
        const selectContainerColor = colors?.activeText
            ? colors.activeText
            : "#091e42";
        const selectContainerStyles = css({
            "justifyContent": "center",
            [media.below.sm]: {
                display: "flex"
            },
            [media.above.sm]: {
                display: "none"
            },
            // This line is being affected by new linting rules, no edits have been made to the code
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ".select-container-inner": {
                position: "relative",
                width: "100%",
                maxWidth: "420px",
                // This line is being affected by new linting rules, no edits have been made to the code
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                svg: {
                    position: "absolute",
                    insetInlineEnd: "20px",
                    insetBlockStart: 0,
                    insetBlockEnd: 0,
                    margin: "auto",
                    color: `${selectContainerColor}`
                }
            }
        });

        return (
            <div className="select-container" css={selectContainerStyles}>
                <div className="select-container-inner">
                    <label htmlFor={id} aria-label={label}>
                        <TabSelect
                            id={id}
                            currentTab={currentTab}
                            tabs={tabs}
                            colors={colors}
                            updateURL={updateURL}
                            setCurrentTab={setCurrentTab}
                            dataProductKey={dataProductKey}
                            onMobileChangeHandler={onMobileChangeHandler}
                        />
                    </label>

                    <svg
                        width="15"
                        height="8"
                        viewBox="0 0 15 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M1.5 1L7.5 7L13.5 1"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>
        );
    };

    return (
        <div
            className="tab-group"
            css={css({
                maxWidth: "100%"
            })}
        >
            {/* desktop pill display */}

            <div css={TabGroupStyle} role="tablist">
                <Row>
                    <div className="tab-group-inner">
                        {tabs.map((tab: TabsTypes, index: number) => {
                            return (
                                <TabGroupInner
                                    key={`tab-${index}-${tab.id}`}
                                    tab={tab}
                                    index={index}
                                    colors={colors}
                                    dataProductKey={dataProductKey}
                                    onDesktopClickHandler={
                                        onDesktopClickHandler
                                    }
                                    setCurrentTab={setCurrentTab}
                                    currentTab={currentTab}
                                />
                            );
                        })}
                    </div>
                </Row>
            </div>

            {/* mobile select */}
            <Row>
                <MobileSelect />
            </Row>

            {/* tab panel content */}
            <div className="tab-panels">
                <div
                    className="tab-panel"
                    css={tabPanel}
                    key={`tabpanel-${currentTab}-${tabsMap[currentTab]?.id}`}
                    aria-hidden={false}
                    id={`tab-${tabsMap[currentTab]?.id}`}
                >
                    {TabContent(
                        tabContentType,
                        tabsMap[currentTab] as TabsTypes,
                        tabs.length,
                        colors
                    )}
                </div>
            </div>
        </div>
    );
};
